import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const UserRegistrationForm = ({ onComplete }) => {
  const { user, logout } = useAuth();
  
  // 中学校選択肢
  const schoolOptions = [
    '開成',
    '桜蔭',
    '筑大駒場',
    '麻布',
    '駒場東邦',
    '海城',
    '筑大附',
    '女子学院',
    '雙葉',
    '豊島岡',
    '聖光学院',
    '栄光学園',
    '渋谷幕張',
    '渋教渋谷',
    '早稲田',
    'その他'
  ];
  const [formData, setFormData] = useState({
    familyName: '',
    givenName: '',
    birthMonth: '',
    birthDay: '',
    schoolName: '',
    customSchoolName: '',
    grade: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // バリデーション
    const finalSchoolName = formData.schoolName === 'その他' ? formData.customSchoolName : formData.schoolName;
    if (!formData.familyName || !formData.givenName || !formData.birthMonth || !formData.birthDay || !finalSchoolName || !formData.grade) {
      setError('すべての項目を入力してください。');
      setIsSubmitting(false);
      return;
    }

    try {
      console.log('Registration attempt with user:', user);
      console.log('Registration data:', {
        full_name: `${formData.familyName} ${formData.givenName}`,
        given_name: formData.givenName,
        family_name: formData.familyName,
        birth_month: parseInt(formData.birthMonth),
        birth_day: parseInt(formData.birthDay),
        school_name: finalSchoolName,
        grade: parseInt(formData.grade)
      });

      const response = await fetch(`${process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '/api'}/users/${user.id}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: `${formData.familyName} ${formData.givenName}`,
          given_name: formData.givenName,
          family_name: formData.familyName,
          birth_month: parseInt(formData.birthMonth),
          birth_day: parseInt(formData.birthDay),
          school_name: finalSchoolName,
          grade: parseInt(formData.grade)
        })
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Response error:', errorData);
        throw new Error(`HTTP ${response.status}: ${errorData}`);
      }

      const result = await response.json();
      console.log('Registration result:', result);
      
      if (result.success) {
        onComplete();
      } else {
        setError('登録に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(`登録に失敗しました: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!user) return null;

  return (
    <Container maxWidth="sm" sx={{ paddingTop: 4 }}>
      <Paper sx={{ padding: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
          <Avatar
            src={user.picture}
            alt={user.name}
            sx={{ width: 80, height: 80, marginBottom: 2 }}
          />
          <Typography variant="h4" gutterBottom>
            初回登録
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            学習を始める前に、基本情報を登録してください。
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={logout}
            sx={{ marginTop: 1 }}
          >
            ログアウト
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="氏名（姓）"
            value={formData.familyName}
            onChange={(e) => handleChange('familyName', e.target.value)}
            margin="normal"
            required
            helperText="例: 田中"
          />

          <TextField
            fullWidth
            label="氏名（名）"
            value={formData.givenName}
            onChange={(e) => handleChange('givenName', e.target.value)}
            margin="normal"
            required
            helperText="例: 太郎"
          />

          <Box sx={{ display: 'flex', gap: 2, marginTop: 2, marginBottom: 1 }}>
            <FormControl sx={{ minWidth: 120 }} required>
              <InputLabel id="birth-month-label">誕生日（月）</InputLabel>
              <Select
                labelId="birth-month-label"
                value={formData.birthMonth}
                label="誕生日（月）"
                onChange={(e) => handleChange('birthMonth', e.target.value)}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                  <MenuItem key={month} value={month}>
                    {month}月
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 120 }} required>
              <InputLabel id="birth-day-label">誕生日（日）</InputLabel>
              <Select
                labelId="birth-day-label"
                value={formData.birthDay}
                label="誕生日（日）"
                onChange={(e) => handleChange('birthDay', e.target.value)}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <MenuItem key={day} value={day}>
                    {day}日
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <FormControl fullWidth margin="normal" required>
            <InputLabel id="school-label">中学校名</InputLabel>
            <Select
              labelId="school-label"
              value={formData.schoolName}
              label="中学校名"
              onChange={(e) => handleChange('schoolName', e.target.value)}
            >
              {schoolOptions.map((school) => (
                <MenuItem key={school} value={school}>
                  {school}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {formData.schoolName === 'その他' && (
            <TextField
              fullWidth
              label="中学校名（その他）"
              value={formData.customSchoolName}
              onChange={(e) => handleChange('customSchoolName', e.target.value)}
              margin="normal"
              required
              placeholder="中学校名を入力してください"
            />
          )}

          <FormControl fullWidth margin="normal" required>
            <InputLabel id="grade-label">学年</InputLabel>
            <Select
              labelId="grade-label"
              value={formData.grade}
              label="学年"
              onChange={(e) => handleChange('grade', e.target.value)}
            >
              <MenuItem value={1}>1年生</MenuItem>
              <MenuItem value={2}>2年生</MenuItem>
              <MenuItem value={3}>3年生</MenuItem>
            </Select>
          </FormControl>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isSubmitting}
            sx={{ marginTop: 3, marginBottom: 2 }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={24} sx={{ marginRight: 1 }} />
                登録中...
              </>
            ) : (
              '登録完了'
            )}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default UserRegistrationForm;